/**
 * Subdomain selection grid — reusable.
 *
 * Renders a checklist of discovered subdomains for a single domain
 * (primary or additional). Pure UI component — its caller owns the
 * selection state, the toggle dispatch, and the "clear all" dispatch.
 *
 * Pool semantics
 * --------------
 * The pricing engine treats subdomains as a SINGLE pool across the
 * entire cart (5 free total, not 5-per-domain — see
 * apps/api/app/services/pricing.py `calculate_cart_price`). That
 * means the usage meter and the per-row marginal-cost badges must
 * reflect the GLOBAL state, not just this grid's own selection.
 * The caller passes `globalSelectedCount` so every grid on the page
 * shows the same shared budget.
 *
 * No pre-selection: rows arrive unchecked by buyer requirement
 * ("mindfully choose what they want to include"). Per-row badges
 * flip live as toggles cross the threshold.
 */

import { useMemo } from "react";

import type { DiscoveredAsset } from "../api";
import {
  RECON_SUBDOMAIN_FREE_THRESHOLD,
  EXTRA_SUBDOMAIN_PRICE_LABEL,
} from "../constants";

import "./subdomain-grid.css";

interface Props {
  /** All discovered hostnames for this domain (apex included; the
   *  grid filters it out). */
  assets: DiscoveredAsset[];
  /** The apex domain — its hostname is excluded from the grid. */
  apex: string;
  /** Set of hostnames (lowercased) currently selected from THIS
   *  domain's discovery. */
  selectedSet: Set<string>;
  /** Toggle one subdomain in/out of the selection. */
  onToggle: (hostname: string) => void;
  /** Clear all selections from this grid. */
  onClearAll: () => void;
  /** Total subdomains selected across the WHOLE cart (this grid's
   *  selection + every other grid's selection). Drives the meter
   *  and per-row marginal-cost badges. */
  globalSelectedCount: number;
  /** Optional heading variant: full hero in the primary card, compact
   *  in the additional-asset cards. */
  variant?: "primary" | "additional";
  /** Optional id prefix for ARIA — every instance on the page needs
   *  unique meta ids since hostnames can repeat across domains. */
  idPrefix?: string;
}

export function SubdomainGrid({
  assets,
  apex,
  selectedSet,
  onToggle,
  onClearAll,
  globalSelectedCount,
  variant = "primary",
  idPrefix = "primary",
}: Props) {
  const apexLower = apex.trim().toLowerCase();

  // Filter out the apex itself — the apex is its own cart line.
  // Sort: alive first, then alpha. Live surface is where attackers
  // focus, so it gets visual primacy.
  const subdomains = useMemo(
    () =>
      assets
        .filter((a) => a.hostname.toLowerCase() !== apexLower)
        .sort((a, b) => {
          if (a.alive !== b.alive) return a.alive ? -1 : 1;
          return a.hostname.localeCompare(b.hostname);
        }),
    [assets, apexLower],
  );

  const localSelectedCount = selectedSet.size;
  const globalFreeRemaining = Math.max(
    0,
    RECON_SUBDOMAIN_FREE_THRESHOLD - globalSelectedCount,
  );

  // Empty state: discovery succeeded but only the apex came back.
  if (subdomains.length === 0) {
    return (
      <section
        className={`subdomain-grid subdomain-grid-empty subdomain-grid-${variant}`}
        aria-labelledby={`${idPrefix}-empty-heading`}
      >
        <h3 id={`${idPrefix}-empty-heading`}>No subdomains found</h3>
        <p>
          We didn't surface any subdomains for this domain. Scope stays
          on the apex itself.
        </p>
      </section>
    );
  }

  return (
    <section
      className={`subdomain-grid subdomain-grid-${variant}`}
      aria-labelledby={`${idPrefix}-heading`}
    >
      <header className="subdomain-grid-header">
        <div>
          <h3 id={`${idPrefix}-heading`}>
            Subdomains found ({subdomains.length})
          </h3>
          {variant === "primary" && (
            <p className="subdomain-grid-help">
              Pick the ones you want investigated. Each selected
              subdomain gets the same treatment as the apex —
              fingerprinted, scanned for vulnerabilities, tested for
              misconfiguration, checked against breach corpora. The
              base assessment covers{" "}
              <strong>{RECON_SUBDOMAIN_FREE_THRESHOLD}</strong>{" "}
              subdomains across your whole engagement; additional
              subdomains are{" "}
              <strong>{EXTRA_SUBDOMAIN_PRICE_LABEL}</strong> each.
            </p>
          )}
        </div>
        <UsageMeter globalSelectedCount={globalSelectedCount} />
      </header>

      <div className="subdomain-grid-actions">
        <button
          type="button"
          className="subdomain-grid-link-btn"
          onClick={onClearAll}
          disabled={localSelectedCount === 0}
        >
          Clear all
        </button>
      </div>

      <ul className="subdomain-grid-list" role="list">
        {subdomains.map((sub) => {
          const lower = sub.hostname.toLowerCase();
          const isChecked = selectedSet.has(lower);
          // Marginal cost: if this row is unchecked, would toggling
          // it on cost $0 or $15? Driven by the GLOBAL free pool, not
          // this grid's local selection — the pricing engine doesn't
          // care which domain a subdomain belongs to.
          const wouldBeFree = isChecked
            ? // Conservative: as long as the global selected count is
              // at or below threshold, all selected rows are free. The
              // pricing engine doesn't track a stable per-row position,
              // so this approximation gives a clean visual.
              globalSelectedCount <= RECON_SUBDOMAIN_FREE_THRESHOLD
            : globalFreeRemaining > 0;

          return (
            <li key={lower} className="subdomain-grid-row">
              <label className="subdomain-grid-row-label">
                <input
                  type="checkbox"
                  className="subdomain-grid-checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(sub.hostname)}
                  aria-describedby={`subdomain-meta-${idPrefix}-${lower}`}
                />
                <span className="subdomain-grid-row-main">
                  <span className="subdomain-grid-hostname">
                    {sub.hostname}
                  </span>
                  <span
                    id={`subdomain-meta-${idPrefix}-${lower}`}
                    className="subdomain-grid-meta"
                  >
                    {sub.alive && (
                      <span
                        className="subdomain-grid-live-dot"
                        aria-hidden="true"
                      />
                    )}
                    {sub.alive ? "Live" : "Not responding"}
                    {sub.ip && <> · {sub.ip}</>}
                  </span>
                </span>
                <PriceBadge isChecked={isChecked} wouldBeFree={wouldBeFree} />
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Sub-components                                                              //
// --------------------------------------------------------------------------- //

interface UsageMeterProps {
  globalSelectedCount: number;
}

function UsageMeter({ globalSelectedCount }: UsageMeterProps) {
  const threshold = RECON_SUBDOMAIN_FREE_THRESHOLD;
  const usedFree = Math.min(globalSelectedCount, threshold);
  const overage = Math.max(0, globalSelectedCount - threshold);
  const freeRemaining = Math.max(0, threshold - globalSelectedCount);

  return (
    <div className="subdomain-grid-meter" aria-live="polite">
      <div className="subdomain-grid-meter-headline">
        <strong>{usedFree}</strong> of {threshold} free
        {overage > 0 && (
          <span className="subdomain-grid-meter-overage">
            {" "}
            · +{overage} at {EXTRA_SUBDOMAIN_PRICE_LABEL}
          </span>
        )}
      </div>
      <div
        className="subdomain-grid-meter-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={threshold}
        aria-valuenow={usedFree}
        aria-label={`${usedFree} of ${threshold} free subdomain slots used across your cart`}
      >
        <div
          className="subdomain-grid-meter-fill"
          style={{
            width: `${(usedFree / threshold) * 100}%`,
          }}
        />
      </div>
      {freeRemaining > 0 ? (
        <div className="subdomain-grid-meter-sub">
          {freeRemaining} free slot{freeRemaining === 1 ? "" : "s"} left
        </div>
      ) : (
        <div className="subdomain-grid-meter-sub subdomain-grid-meter-sub-warn">
          Free slots used — additions are {EXTRA_SUBDOMAIN_PRICE_LABEL}
        </div>
      )}
    </div>
  );
}

interface PriceBadgeProps {
  isChecked: boolean;
  wouldBeFree: boolean;
}

function PriceBadge({ isChecked, wouldBeFree }: PriceBadgeProps) {
  if (isChecked) {
    return (
      <span
        className={
          wouldBeFree
            ? "subdomain-grid-badge subdomain-grid-badge-free"
            : "subdomain-grid-badge subdomain-grid-badge-extra"
        }
      >
        {wouldBeFree ? "Free" : EXTRA_SUBDOMAIN_PRICE_LABEL}
      </span>
    );
  }
  return (
    <span
      className={
        wouldBeFree
          ? "subdomain-grid-badge subdomain-grid-badge-free-hint"
          : "subdomain-grid-badge subdomain-grid-badge-extra-hint"
      }
    >
      {wouldBeFree ? "+ Free" : `+ ${EXTRA_SUBDOMAIN_PRICE_LABEL}`}
    </span>
  );
}
